<template>
  <transition enter-active-class="animated fadeIn">
    <div class="candidate speech-bubble" v-if="show">
      <div
        class="speech-bubble-container"
        :class="{
          'mt-3': $vuetify.breakpoint.smAndUp && (typing || loading),
        }"
      >
        <v-layout row :justify-start="!alt" :justify-end="alt">
          <!-- Text / Content -->
          <div v-if="showText" class="speech-bubble-text-container">
            <div
              class="speech-bubble-text"
              :class="[{ alt: alt }, color]"
              :style="{ '--arrow-color': arrowColor, 'min-width': minWidth }"
            >
              <v-card flat v-if="!typing && !loading" :dark="dark" class="transparent" style="font-size: 18px">
                <v-card-text  v-if="message && (typeof message === 'function' || message.length)">
                  {{ typeof message === 'function' ? message() : message }}
                </v-card-text>
                <v-card-text v-if="$slots.default" class="pt-0">
                  <slot name="default"></slot>
                </v-card-text>
                <div class="icon" v-if="isChecked">
                  <v-icon :dark="dark" small>mdi-check-all</v-icon>
                </div>
                <!-- Tooltip help -->
                <div v-if="$slots.help">
                  <v-menu offset-y max-width="320">
                    <v-btn icon slot="activator" :color="themes.candidate.light.mutedText" small>
                      <v-icon>mdi-help-circle</v-icon>
                    </v-btn>
                    <v-card>
                      <v-card-text> <slot name="help"></slot> </v-card-text>
                    </v-card>
                  </v-menu>
                </div>
              </v-card>
            </div>
          </div>

          <!-- Typing Indicator -->
          <div v-else>
            <div
              class="speech-bubble-text"
              :class="[{ alt: alt }, color]"
              :style="{ '--arrow-color': arrowColor, 'min-width': minWidth }"
            >
              <div class="typing">
                <i :class="dark ? 'white' : themes.candidate.light.muted"></i>
                <i :class="dark ? 'white' : themes.candidate.light.muted"></i>
                <i :class="dark ? 'white' : themes.candidate.light.muted"></i>
              </div>
            </div>
          </div>
        </v-layout>
      </div>

      <!-- Avatar -->
      <div class="absolute-avatar">
        <div :class="{ 'speech-bubble-avatar': !alt, 'speech-bubble-avatar-alt': alt }">
          <slot
            name="avatar"
            v-if="$vuetify.breakpoint.smAndUp && (avatar || typeof avatar === 'string')"
          >
            <v-avatar size="50" :color="themes.candidate.light.dialogTitle">
              <v-avatar size="48">
                <v-img
                  :src="avatar.length ? avatar : '/img/leaply-rainbow-square.png'"
                  @load="avatarLoaded = true"
                  max-width="48"
                  contain
                />
              </v-avatar>
            </v-avatar>
          </slot>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
// Mixins
import { colorStringToHex } from '@/mixins/colorStringToHex.js'

// Libraries
import colors from '@/data/colors'

export default {
  mixins: [colorStringToHex],

  created() {
    setTimeout(() => {
      this.show = true
      this.typingToggle()
    }, this.delay)
  },

  props: {
    alt: { type: Boolean, default: () => false },
    avatar: { type: [Boolean, String], default: () => false },
    color: { type: String, default: () => 'grey lighten-4' },
    dark: { type: Boolean, default: () => false },
    delay: { type: Number, default: () => (process.env.NODE_ENV === 'development' ? 0 : 800) },
    isChecked: { type: Boolean, default: () => false },
    message: { type: [Function, String], default: () => '' },
    minWidth: { type: String, default: () => '0px' },
    showTyping: { type: Boolean, default: () => true },
    loading: { type: Boolean, default: () => false },
  },

  data() {
    return {
      colors,
      show: false,
      typing: true,
      avatarLoaded: false,
    }
  },

  computed: {
    showText() {
      if (this.$vuetify.breakpoint.smAndUp) {
        return !this.typing && !this.loading && (this.avatarLoaded || !this.avatar)
      }

      return !this.typing && !this.loading
    },

    arrowColor() {
      return this.colorStringToHex(this.color)
    },
  },

  methods: {
    typingDuration() {
      if (!this.showTyping || process.env.NODE_ENV === 'development') return 0
      if (typeof this.message === 'function' && this.message().length) {
        return Math.min(this.message().length * 50, 2000)
      }

      return Math.min(this.message.length * 50, 2000)
    },

    typingToggle() {
      this.typing = true
      setTimeout(() => {
        this.typing = false
        this.$emit('display')
      }, this.typingDuration())
    },
  },
}
</script>

<style lang="scss">
.candidate.speech-bubble {
  .absolute-avatar {
    position: relative;

    .speech-bubble-avatar {
      position: absolute;
      bottom: 0px;
      transform: translateY(20%);
    }
  }

  & .speech-bubble-container {
    display: block;
    padding: 5px 0rem;
    width: 100%;

    @media (min-width: 600px) {
      padding: 16px 2rem;
    }
  }

  & .speech-bubble-text-container {
    width: 100%;
  }

  & .speech-bubble-text {
    box-sizing: border-box;
    display: inline-block;
    position: relative;
    margin: 0 0 0 2.5rem;
    max-width: 75%;

    &.alt {
      margin: 0 2.5rem 0 0;
    }

    -webkit-border-radius: 10px;
    -moz-border-radius: 10px;
    border-radius: 10px;

    -webkit-box-shadow: 0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14),
      0px 1px 3px 0px rgba(0, 0, 0, 0.12);
    -moz-box-shadow: 0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14),
      0px 1px 3px 0px rgba(0, 0, 0, 0.12);
    box-shadow: 0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14),
      0px 1px 3px 0px rgba(0, 0, 0, 0.12);

    &:before,
    &:after {
      border-radius: 20px / 5px;
      content: '';
      display: block;
      position: absolute;
    }

    &:before {
      border: 9px solid transparent;
      border-bottom-color: rgba(0, 0, 0, 0.15);
      bottom: 3px;
      left: -10px;
      transform: rotate(5deg);
    }

    &:after {
      border: 15px solid transparent;
      border-bottom-color: var(--arrow-color);
      bottom: 3px;
      left: -10px;
    }

    &.alt,
    &.alt .icon {
      float: right;
    }

    &.alt:before {
      border-bottom-color: rgba(0, 0, 0, 0.45);
      left: auto;
      bottom: 3px;
      right: -10px;
      transform: rotate(-5deg);
    }

    &.alt:after {
      left: auto;
      right: -11px;
    }
  }

  & .icon {
    padding: 0 0.5rem;
  }

  & .typing {
    display: inline-block;
    padding: 3px 10px;

    & i {
      border-radius: 50%;
      display: inline-block;
      height: 10px;
      margin-right: 3px;
      width: 10px;
    }

    & i:nth-child(1) {
      animation: typing 1s infinite ease-in;
    }

    & i:nth-child(2) {
      animation: typing 1s 0.25s infinite ease-in;
    }

    & i:nth-child(3) {
      animation: typing 1s 0.5s infinite ease-in;
    }
  }

  .grow-right-enter-active {
    animation: grow 0.3s ease-in-out;
    transform-origin: bottom left;
  }

  .grow-right-leave-active {
    animation: grow 0.1s ease-in-out reverse;
    transform-origin: bottom left;
  }

  .grow-left-enter-active {
    animation: grow 0.3s ease-in-out;
    transform-origin: bottom right;
  }

  .grow-left-leave-active {
    animation: grow 0.01s ease-in-out reverse;
    transform-origin: bottom right;
  }

  .grow-right {
    animation: grow 0.2s linear;
    transform-origin: bottom left;
    overflow: hidden;
  }

  .grow-left {
    animation: grow 0.2s linear;
    transform-origin: bottom right;
    overflow: hidden;
  }
}
@keyframes grow {
  0% {
    transform: scale(0.7);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes typing {
  0% {
    opacity: 0.75;
    transform: scale(0.5);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
  67% {
    opacity: 0.75;
    transform: scale(0.5);
  }
  100% {
    opacity: 0.75;
    transform: scale(0.5);
  }
}
</style>
